<template>
    <div>
        <div class="pb-10 md:max-w-1/2">
            <h2 class="mb-6">Update User Account</h2>

            <form-wrapper :validator="$v.user">

                <form-group name="first_name" label="First name" class="mb-14">
                    <input
                        @input="delayTouch($v.user.first_name)"
                        class="md:max-w-2/3"
                        slot-scope="{attributes}"
                        v-model="user.first_name"
                        v-bind="attributes"
                        type="text"
                        placeholder="Enter first name..."
                    >
                </form-group>

                <form-group name="last_name" label="Last name" class="mb-14">
                    <input
                        @input="delayTouch($v.user.last_name)"
                        class="md:max-w-2/3"
                        slot-scope="{attributes}"
                        v-model="user.last_name"
                        v-bind="attributes"
                        type="text"
                        placeholder="Enter last name..."
                    >
                </form-group>

                <form-group name="email" label="Email" class="mb-14">
                    <input
                        @input="delayTouch($v.user.email)"
                        class="md:max-w-2/3"
                        slot-scope="{attributes}"
                        v-model="user.email"
                        v-bind="attributes"
                        type="text"
                        placeholder="Enter email..."
                    >
                </form-group>

                <form-group name="password" label="Password" class="mb-14">
                    <input
                        @input="delayTouch($v.user.password)"
                        class="md:max-w-2/3"
                        slot-scope="{attributes}"
                        v-model="user.password"
                        v-bind="attributes"
                        type="password"
                    >
                </form-group>

                <form-group name="password_confirmation" label="Password confirmation" class="mb-14">
                    <input
                        @input="delayTouch($v.user.password_confirmation)"
                        class="md:max-w-2/3"
                        slot-scope="{attributes}"
                        v-model="user.password_confirmation"
                        v-bind="attributes"
                        type="password"
                    >
                </form-group>

                <form-group name="position" label="Position" class="mb-14">
                    <input
                        @input="delayTouch($v.user.position)"
                        class="md:max-w-2/3"
                        slot-scope="{attributes}"
                        v-model="user.position"
                        v-bind="attributes"
                        type="text"
                        placeholder="Enter position..."
                    >
                </form-group>

                <div class="md:ml-auto md:w-2/3">
                    <button @click="onSelectImage" class="w-full mb-14">
                        <span v-if="imageSrc || imageCroppedSrc">Change image</span>
                        <span v-else>Select image</span>
                        <input ref="fileInput" @input="setImage" type="file" name="image" accept="image/*" class="hidden" />
                    </button>

                    <template v-if="imageSrc">
                        <vue-cropper
                            :aspect-ratio="1"
                            :auto-crop-area="1"
                            :guides="true"
                            :src="imageSrc"
                            class="w-full mr-2 mb-14"
                            ref="cropper"
                        ></vue-cropper>

                        <button @click="cropImage" class="w-full mb-14">Crop image</button>
                    </template>

                    <!-- Cropped image previewer -->
                    <img v-if="imageCroppedSrc" :src="imageCroppedSrc" class="w-full mb-14"/>
                </div>

            </form-wrapper>

            <div v-if="errors" class="flex flex-col px-6 pt-6 pb-4 my-6 bg-red-700">
                <span v-for="error in errors" class="mb-2 text-white">
                    {{error[0]}}
                </span>
            </div>
        </div>

        <div class="flex justify-end md:max-w-1/2">
            <button @click="onUpdate" :disabled="!canUpdate" class="ml-4">
                <span v-if="updating">Updating...</span>
                <span v-else>Update</span>
            </button>
        </div>
    </div>
</template>

<script>
    import { between, email, integer, maxLength, minLength, required, sameAs } from "vuelidate/lib/validators"
    import { call, get, sync } from 'vuex-pathify'
    import imageUploadMixin from "../mixins/imageUpload"
    import Multiselect from "vue-multiselect"
    import validationMixin from "../mixins/validation"

    export default {
        props: {
            userId: {
                type: Number,
                required: true,
            }
        },

        mixins: [
            imageUploadMixin,
            validationMixin
        ],

        components: {
            Multiselect,
        },

        data() {
            return {
                errors: null,

                updating: false,
            }
        },

        computed: {
            user: sync('users/user'),
            clients: get('clients/clients'),

            canUpdate() {
                if (!this.$v.$dirty) {
                    return !this.updating
                }

                return !this.$v.$invalid && !this.updating
            },
        },

        async mounted() {
            this.getUser()
        },

        methods: {
            loadUser: call('users/load'),
            reset: call('users/resetUser'),
            update: call('users/update'),
            updateAvatar: call('users/updateAvatar'),

            close() {
                this.$v.$reset()

                this.updating = false
            },

            async getUser() {
                await this.loadUser(this.userId)

                this.user.password = ''
                this.user.password_confirmation = ''

                this.imageSrc = null
                this.imageCroppedSrc = this.user.avatar
            },

            async onUpdate() {
                if (! this.$v.$invalid) {
                    this.updating = true

                    if (!Boolean(this.user.password)) {
                        delete this.user.password
                        delete this.user.password_confirmation
                    }

                    try {
                        await this.update(this.user)

                        if (this.user.avatar !== this.imageCroppedSrc) {
                            await this.uploadImage(this.user)
                        }

                        this.$emit('update')
                    } catch (error) {
                        this.errors = error.response.data.errors

                        this.$v.$reset()
                    }
                } else {
                    this.$v.$touch();
                }
            },

            async updateImage(formData) {
                return await this.updateAvatar(formData)
            }
        },

        validations: {
            user: {
                default_client: {
                    required,
                },

                first_name: {
                    required,
                    minLength: minLength(3),
                    maxLength: maxLength(255)
                },

                last_name: {
                    minLength: minLength(3),
                    maxLength: maxLength(255)
                },

                position: {
                    minLength: minLength(3),
                    maxLength: maxLength(255)
                },

                email: {
                    required,
                    email
                },

                password: {
                    minLength: minLength(6),
                    maxLength: maxLength(255)
                },
                password_confirmation: {
                    sameAsPassword: sameAs('password')
                },
            }
        },

        beforeDestroy() {
            this.close()
        },
    }
</script>
